import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import StockHistoryModal from './StockHistoryModal';

const StockReport = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [lastPage, setLastPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showStockHistoryModal, setShowStockHistoryModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    // Filter options from API
    const [filters, setFilters] = useState({
        locations: [],
        categories: [],
        brands: [],
        units: []
    });

    // Selected filter values
    const [selectedFilters, setSelectedFilters] = useState({
        location_id: '',
        category_id: '',
        brand_id: '',
        unit_id: '',
        type: '',
        active_state: ''
    });

    // Fetch filters on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const res = await apiService.getStockReportFilters();
                if (res.success) {
                    setFilters(res.data);
                }
            } catch (err) {
      /* console.error('Failed to load filters:', err); */
            }
        };
        fetchFilters();
    }, []);

    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        try {
            let productId = null;
            
            // If there's a search term, try to resolve it to a product ID first
            if (searchTerm && searchTerm.trim()) {
                try {
                    const prodRes = await apiService.getProducts({ search: searchTerm.trim(), per_page: 5 });
                    const prodList = prodRes.data || (Array.isArray(prodRes) ? prodRes : []);
                    
                    if (prodList.length > 0) {
                        // Find exact SKU match if possible, otherwise use the first result
                        const exactMatch = prodList.find(p => String(p.sku).toLowerCase() === searchTerm.trim().toLowerCase());
                        productId = exactMatch ? exactMatch.id : prodList[0].id;
                    } else {
                        // No product found for this search term, return empty report
                        setReportData([]);
                        setTotalEntries(0);
                        setLastPage(1);
                        setIsLoading(false);
                        return;
                    }
                } catch (err) {
      /* console.error('Error resolving product search:', err); */
                }
            }

            const params = {
                ...selectedFilters,
                per_page: perPage,
                page: currentPage,
                product_id: productId
            };
            
            const res = await apiService.getStockReport(params);
            if (res.success) {
                // Handle both { data: { data: [] } } and { data: [] }
                const reportList = res.data?.data || (Array.isArray(res.data) ? res.data : []);
                setReportData(reportList);
                
                // Handle pagination from res.data or root
                setTotalEntries(res.data?.total || res.total || reportList.length || 0);
                setLastPage(res.data?.last_page || res.last_page || 1);
            }
        } catch (err) {
      /* console.error('Failed to fetch stock report:', err); */
        } finally {
            setIsLoading(false);
        }
    }, [selectedFilters, perPage, currentPage, searchTerm]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // Metrics calculation (can be derived from report data or a separate API if available)
    const metrics = {
        totalStockValuePurchase: reportData.reduce((sum, item) => sum + (parseFloat(item.stock_price) || 0), 0),
        totalStockValueSale: reportData.reduce((sum, item) => sum + (parseFloat(item.stock_value_by_sale_price) || 0), 0),
        potentialProfit: reportData.reduce((sum, item) => sum + (parseFloat(item.potential_profit) || 0), 0)
    };
    const avgProfitMargin = metrics.totalStockValuePurchase > 0 
        ? (metrics.potentialProfit / metrics.totalStockValuePurchase * 100) 
        : 0;

    const handleExport = (format) => {
        const headers = [
            'SKU', 'Product', 'Variation', 'Category', 'Location', 
            'Unit Selling Price', 'Current stock', 
            'Current Stock Value (By purchase price)', 
            'Current Stock Value (By sale price)', 
            'Potential profit'
        ];
        
        const rows = reportData.map(p => [
            p.sku,
            p.product,
            p.variation_name || 'Default',
            p.category_name,
            p.location_name,
            p.unit_price_formatted,
            p.stock_formatted,
            p.stock_price_formatted,
            p.stock_value_by_sale_price_formatted,
            p.potential_profit_formatted
        ]);

        const title = 'Stock Report';
        const filename = 'stock_report';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
    };

    const handleOpenStockHistory = (p) => {
        setSelectedProduct({ ...p, id: p.product_id, name: p.product });
        setShowStockHistoryModal(true);
    };

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* Metrics Grid */}
            <div className="stock-report-metrics">
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Closing stock (By purchase price)</div>
                    <div className="stock-report-metrics-value">$ {metrics.totalStockValuePurchase.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Closing stock (By sale price)</div>
                    <div className="stock-report-metrics-value">$ {metrics.totalStockValueSale.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Potential profit</div>
                    <div className="stock-report-metrics-value" style={{ color: '#10b981' }}>$ {metrics.potentialProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="stock-report-metrics-item">
                    <div className="stock-report-metrics-label">Profit Margin %</div>
                    <div className="stock-report-metrics-value" style={{ color: '#3b82f6' }}>{avgProfitMargin.toFixed(2)}%</div>
                </div>
            </div>

            {/* Table Section */}
            <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1.25rem' }}>
                    <div className="table-controls-left">
                        <div className="entries-dropdown">
                            <span>Show</span>
                            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>

                    <div className="table-controls-right">
                        <div className="tabs-search-wrapper">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="tabs-search-input"
                            />
                            <button
                                className="filter-toggle-btn"
                                onClick={() => setShowFilters(true)}
                                title="Open Filters"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                                </svg>
                                Filters
                            </button>
                        </div>
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu">
                                    <button onClick={() => { handleExport('CSV'); setShowExportDropdown(false); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                                    <button onClick={() => { handleExport('Excel'); setShowExportDropdown(false); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                                    <button onClick={() => { handleExport('Print'); setShowExportDropdown(false); }}><i className="fa fa-print"></i> Print</button>
                                    <button onClick={() => { handleExport('PDF'); setShowExportDropdown(false); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="products-table-container" style={{ overflowX: 'auto' }}>
                    {isLoading ? (
                        <div className="loading-sales">
                            <h3>⏳ Loading Stock Report...</h3>
                            <p>Fetching stock report data from server...</p>
                        </div>
                    ) : (
                    <table className="products-table" style={{ minWidth: '1300px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '120px' }}>Action</th>
                                <th>SKU</th>
                                <th>Product</th>
                                <th>Variation</th>
                                <th>Category</th>
                                <th>Location</th>
                                <th>Unit Selling Price</th>
                                <th>Current stock</th>
                                <th>Current Stock Value (Purchase)</th>
                                <th>Current Stock Value (Sale)</th>
                                <th>Potential profit</th>
                                <th>Total unit sold</th>
                                <th>Total Unit Transfered</th>
                                <th>Total Unit Adjusted</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.length === 0 ? (
                                <tr><td colSpan="14" style={{ textAlign: 'center', padding: '3rem', color: 'var(--etpos-text-muted, #64748b)' }}>No stock data found</td></tr>
                            ) : (
                                reportData.map((p, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <button className="stock-history-btn" onClick={() => handleOpenStockHistory(p)}>
                                                <i className="fa fa-history"></i> Stock history
                                            </button>
                                        </td>
                                        <td>{p.sku}</td>
                                        <td style={{ fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{p.product}</td>
                                        <td>{p.variation_name || 'DUMMY'}</td>
                                        <td>{p.category_name}</td>
                                        <td>{p.location_name}</td>
                                        <td>{p.unit_price_formatted}</td>
                                        <td style={{ fontWeight: '600' }}>
                                            <span style={{ 
                                                padding: '0.2rem 0.5rem', 
                                                background: p.is_below_alert_quantity ? '#fef2f2' : '#f0fdf4',
                                                color: p.is_below_alert_quantity ? '#dc2626' : '#16a34a',
                                                borderRadius: '4px'
                                            }}>
                                                {p.stock_formatted && !p.stock_formatted.startsWith('0.0') ? p.stock_formatted : (p.current_stock || p.stock || p.qty_available || p.stock_formatted || '0')}
                                            </span>
                                        </td>
                                        <td>{p.stock_price_formatted}</td>
                                        <td>{p.stock_value_by_sale_price_formatted}</td>
                                        <td style={{ color: p.potential_profit >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                                            {p.potential_profit_formatted}
                                        </td>
                                        <td>{p.total_sold_formatted}</td>
                                        <td>{p.total_transferred_formatted}</td>
                                        <td>{p.total_adjusted_formatted}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)', background: 'var(--etpos-card, white)' }}>
                    <div style={{ color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.875rem' }}>
                        Showing {reportData.length > 0 ? (currentPage - 1) * perPage + 1 : 0} to {Math.min(currentPage * perPage, totalEntries)} of {totalEntries} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #64748b)' }}>
                            Page {currentPage} of {lastPage}
                        </span>
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(lastPage, prev + 1))}
                            disabled={currentPage === lastPage}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter Modal Popup */}
            {showFilters && (
                <div className="modal-overlay" onClick={() => setShowFilters(false)}>
                    <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
                        <div className="modal-header">
                            <h2>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                                </svg>
                                Filters
                            </h2>
                            <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
                        </div>
                        <div className="modal-content" style={{ padding: '1.5rem' }}>
                            <div className="filter-row">
                                <div className="filter-group">
                                    <label>Business Location:</label>
                                    <select
                                        value={selectedFilters.location_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, location_id: e.target.value })}
                                    >
                                        <option value="">All Locations</option>
                                        {filters.locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Category:</label>
                                    <select
                                        value={selectedFilters.category_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, category_id: e.target.value })}
                                    >
                                        <option value="">All Categories</option>
                                        {filters.categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Brand:</label>
                                    <select
                                        value={selectedFilters.brand_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, brand_id: e.target.value })}
                                    >
                                        <option value="">All Brands</option>
                                        {filters.brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Unit:</label>
                                    <select
                                        value={selectedFilters.unit_id}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, unit_id: e.target.value })}
                                    >
                                        <option value="">All Units</option>
                                        {filters.units.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="filter-row second-row">
                                <div className="filter-group">
                                    <label>Product Type:</label>
                                    <select
                                        value={selectedFilters.type}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, type: e.target.value })}
                                    >
                                        <option value="">All Types</option>
                                        <option value="single">Single</option>
                                        <option value="variable">Variable</option>
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Active State:</label>
                                    <select
                                        value={selectedFilters.active_state}
                                        onChange={(e) => setSelectedFilters({ ...selectedFilters, active_state: e.target.value })}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stock History Modal */}
            {showStockHistoryModal && (
                <StockHistoryModal
                    product={selectedProduct}
                    onClose={() => setShowStockHistoryModal(false)}
                />
            )}
        </div>
    );
};

export default StockReport;
